import React, { useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { WrenchIcon, SparklesIcon, PaintbrushIcon, ShieldCheckIcon, BeakerIcon, DropletIcon, SprayCanIcon, StarIcon, PackageIcon, BoxIcon, CarIcon, FlaskConicalIcon, ArrowRightIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type ProductLinesProps = {
  language: Language;
  titleOverride?: string;
  variant?: 'static' | 'animated';
};
const categories = {
  ua: [{
    icon: WrenchIcon,
    name: 'Шпаклівка'
  }, {
    icon: SparklesIcon,
    name: 'Лаки'
  }, {
    icon: PaintbrushIcon,
    name: 'Грунти'
  }, {
    icon: ShieldCheckIcon,
    name: 'Захисні покриття'
  }, {
    icon: BeakerIcon,
    name: 'Ready Mix'
  }, {
    icon: FlaskConicalIcon,
    name: 'Розчинники та знежирювачі'
  }, {
    icon: SprayCanIcon,
    name: 'Аерозолі'
  }, {
    icon: StarIcon,
    name: 'Поліролі'
  }, {
    icon: DropletIcon,
    name: 'Клеї та герметики'
  }, {
    icon: BoxIcon,
    name: 'Витратні матеріали'
  }, {
    icon: ShieldCheckIcon,
    name: 'CHA Protect'
  }, {
    icon: CarIcon,
    name: 'Автокосметика'
  }],
  en: [{
    icon: WrenchIcon,
    name: 'Putty'
  }, {
    icon: SparklesIcon,
    name: 'Lacquers'
  }, {
    icon: PaintbrushIcon,
    name: 'Primers'
  }, {
    icon: ShieldCheckIcon,
    name: 'Protective Coatings'
  }, {
    icon: BeakerIcon,
    name: 'Ready Mix'
  }, {
    icon: FlaskConicalIcon,
    name: 'Solvents & Degreasers'
  }, {
    icon: SprayCanIcon,
    name: 'Aerosols'
  }, {
    icon: StarIcon,
    name: 'Polishes'
  }, {
    icon: DropletIcon,
    name: 'Adhesives & Sealants'
  }, {
    icon: BoxIcon,
    name: 'Consumables'
  }, {
    icon: ShieldCheckIcon,
    name: 'CHA Protect'
  }, {
    icon: CarIcon,
    name: 'Auto Cosmetics'
  }]
};
const sectionText = {
  ua: {
    title: 'Каталог продукції',
    subtitle: 'Професійні рішення для автомобільної індустрії',
    viewCategory: 'Переглянути'
  },
  en: {
    title: 'Product Catalog',
    subtitle: 'Professional solutions for automotive industry',
    viewCategory: 'View'
  }
};
export function ProductLines({
  language,
  titleOverride,
  variant = 'static'
}: ProductLinesProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    amount: 0.05
  });
  const t = sectionText[language];
  const categoryList = categories[language];
  return <section ref={ref} id="products" className="relative py-16 sm:py-20 lg:py-32 bg-chamaleon-black" data-id="element-244">
      {/* Subtle background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-245">
        <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} data-id="element-246" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-247">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12 lg:mb-16" data-id="element-248">
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-3 sm:mb-4" data-id="element-249">
            {titleOverride || t.title}
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-250">
            {t.subtitle}
          </p>
          <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-chamaleon-yellow to-transparent mx-auto mt-6" data-id="element-251" />
        </motion.div>

        {/* Categories Grid - 1 column on mobile for animated, 2 for others, 3 on desktop */}
        <div className={`grid ${variant === 'animated' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2'} lg:grid-cols-3 gap-6 sm:gap-8`} data-id="element-252">
          {categoryList.map((category, index) => {
          const Icon = category.icon;
          return <motion.a key={category.name} href="#" initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: index * 0.05
          }} whileHover={{
            y: -8
          }} className="group block" data-id="element-253">
                {/* Premium Card Design */}
                <div className="relative rounded-2xl overflow-hidden h-[280px] sm:h-[360px] group cursor-pointer bg-chamaleon-charcoal" data-id="element-254">
                  {/* Background Image */}
                  <div className="absolute inset-0" data-id="element-255">
                    <img src={['https://cdn.magicpatterns.com/uploads/5u8374uVnBMget9AjsARuh/IMG_5300.webp', 'https://cdn.magicpatterns.com/uploads/vUK9ymMRVxie5HJyWd6gBb/IMG_5301.webp', 'https://cdn.magicpatterns.com/uploads/q8kaZi4kXZzU2mzvPka8TT/IMG_5302.webp', 'https://cdn.magicpatterns.com/uploads/cNkYcQCf9S3xGYNsKW5mPj/IMG_5303.webp', 'https://cdn.magicpatterns.com/uploads/pY5iRBVHwBk92E5UYc5zDL/IMG_5304.webp'][index % 5]} alt={category.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-256" />
                  </div>

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" data-id="element-257" />

                  {/* Content */}
                  <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 flex items-end justify-between z-10 overflow-hidden" data-id="element-258">
                    <h3 className="text-lg sm:text-2xl font-bold text-white pr-4" data-id="element-259">
                      {category.name}
                    </h3>

                    {/* Bottom-right Arrow (always visible) */}
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 text-white transition-all duration-500 group-hover:bg-white/30 group-hover:scale-110" data-id="element-260">
                      <ArrowRightIcon className="w-5 h-5 sm:w-6 sm:h-6 transform rotate-0 group-hover:-rotate-45 transition-transform duration-500 ease-out" data-id="element-261" />
                    </div>
                  </div>

                  {/* Subtle border overlay */}
                  <div className="absolute inset-0 border-2 border-white/0 group-hover:border-white/10 rounded-2xl transition-colors duration-500 pointer-events-none z-20" data-id="element-262" />
                </div>
              </motion.a>;
        })}
        </div>
      </div>
    </section>;
}